// web_components/wcProduct/wcProduct_Utils.ts

import { applyFocusTrap, clearFocusTrap } from '../../utils/focusTrap';
import { dispatchAddToBasket } from '../../events/addToBasketEvent';

/**
 * Matches predefined patterns like 'White Wine Case', 'Mixed Wine Case', and 'Red Wine Case'
 * within the product title and wraps them in a <span> element.
 *
 * @param {string} title - The product title to search for matching patterns.
 * @returns {string} - The product title with matched patterns wrapped in <span> elements.
 */
export function wrapCasePattern(title: string): string {
	// Define the patterns you want to match
	const patterns = ['White Wine Case', 'Mixed Wine Case', 'Red Wine Case', 'in Wooden Gift Box'];

	// Loop through each pattern and replace occurrences with a <span> wrapper
	patterns.forEach((pattern) => {
		const regex = new RegExp(`(${pattern})`, 'g'); // Create a regex to match the pattern globally
		title = title.replace(regex, '<span>$1</span>'); // Wrap the matched pattern in <span>
	});

	return title; // Return the updated title
}

/**
 * Calculates pricing details and returns fixed-2 strings for display.
 *
 * - If `was_price` <= `price`, returns wasPrice as '' and discount 0.
 * - `preorderDeposit` is '' or a "99.00" style string.
 *
 * @param {object} productData
 * @returns {{
 *   nowPrice: string,
 *   wasPrice: string,
 *   discount: number,
 *   preorderDeposit: string
 * }}
 */
export function getPricingDetails(productData: any): {
	nowPrice: string;
	wasPrice: string;
	discount: number;
	preorderDeposit: string;
} {
	const nowNum = parseFloat(productData.price);
	const wasNum = parseFloat(productData.was_price);

	const nowValid = Number.isFinite(nowNum) ? nowNum : 0;
	const wasValid = Number.isFinite(wasNum) ? wasNum : 0;

	let discount = 0;
	let wasPrice = '';

	if (wasValid > 0 && nowValid < wasValid) {
		discount = Math.floor(((wasValid - nowValid) / wasValid) * 100);
		wasPrice = wasValid.toFixed(2);
	}

	let preorderDeposit = '';
	if (productData.preorder_deposit !== '' && productData.preorder_deposit != null) {
		const d = parseFloat(productData.preorder_deposit);
		if (Number.isFinite(d)) preorderDeposit = d.toFixed(2);
	}

	const nowPrice = nowValid.toFixed(2);

	return { nowPrice, wasPrice, discount, preorderDeposit };
}

/**
 * Determines the stock status of the product, including preorder, out-of-stock, and sold-out states.
 * Returns an object containing both the stock status CSS class and the corresponding stock status message.
 *
 * @param {object} productData - The product data.
 * @returns {{stockStatusClass: string, stockStatusMessage: string}} -
 * The stock status information including CSS class and the display message.
 */
export function getStockStatus(productData: any): {
	stockStatusClass: string;
	stockStatusMessage: string;
} {
	// Direct return using a ternary operator for concise logic
	return productData.availability_state === 'preorder'
		? {
				stockStatusClass: 'wc-product__pre-sell-item',
				stockStatusMessage: `Reserve for &pound;${getPricingDetails(productData).preorderDeposit}`,
			}
		: productData.store_stock_level <= 0
			? { stockStatusClass: 'wc-product__out-of-stock', stockStatusMessage: 'Out of stock' }
			: productData.force_sold_out === 'true'
				? { stockStatusClass: 'wc-product__sold-out', stockStatusMessage: 'Sold out' }
				: { stockStatusClass: '', stockStatusMessage: '' };
}

/**
 * Determines the product type and returns both a CSS class and the appropriate image URL.
 * Handles different product types, such as pre-mixed wine cases, single bottles, and advent calendars.
 * Mantiki variables can be used for future logic.
 *
 * @param {object} productData - The product data.
 * @returns {{ productTypeClass: string, productImage: string }} - The product type class and the product image URL.
 */
export function getProductTypeDetails(productData: any): {
	productType: string;
	productTypeClass: string;
	productImage: string;
	productUrl: string;
} {
	let productType = '';
	let productTypeClass = '';
	let productImage = '';
	let productUrl = '';
	let productUrlSegment = '';

	// Helper function to format the size as a three-digit number (e.g., 001, 200, 720)
	const formatSize = (size: number): string => {
		return size.toString().padStart(3, '0');
	};

	// Process the product image by replacing '/000' and changing the file extension
	const processProductImage = (imageUrl: string, size: number): string => {
		const formattedSize = formatSize(size);
		// Replace '/000' with the formatted size and change the file extension
		return imageUrl.replace('/000', `/${formattedSize}`).replace(/\.\w+$/, ``);
	};

	// Switch case for determining the product type class, corresponding image size, and file type
	switch (productData.product_type_name) {
		case 'pre_mixed_wine_cases':
			productUrlSegment = 'wine-case';
			productImage = processProductImage(productData.product_images, 720);
			break;
		case 'wine_bottles':
			productUrlSegment = 'wine';
			productImage = processProductImage(productData.product_images, 200);
			break;
		case 'beer_bottles':
			productUrlSegment = 'beer';
			productImage = processProductImage(productData.product_images, 200);
			break;
		case 'spirits':
			productUrlSegment = 'spirit/' + productData.spirits_category.toLowerCase();
			productImage = processProductImage(productData.product_images, 200);
			break;
		case 'composite_gifts':
			productUrlSegment = 'gifts/product';
			productImage = processProductImage(productData.product_images, 720);
			break;
		case 'advent_calendar':
			productUrlSegment = 'gifts/advent';
			productImage = processProductImage(productData.product_images, 720);
			break;
		case 'event_tickets':
			productUrlSegment = 'event';
			productImage = processProductImage(productData.product_images, 720);
			break;
		default:
			productUrlSegment = productType;
			productImage = processProductImage(productData.product_images, 720);
	}

	productType = productData.product_type_name;
	productTypeClass = 'wc-product__type-' + productType;
	productUrl = window.serverData.contextPath + '/' + productUrlSegment + '/' + productData.url;

	return { productType, productTypeClass, productImage, productUrl };
}

/**
 * Determines whether the product has a special label (e.g., "Best Seller") and returns it.
 *
 * @param {object} productData - The product data.
 * @returns {string} - The special label for the product or an empty string if none exists.
 */
export function getSpecialLabel(productData: any): string {
	return productData.special_label || '';
}

/**
 * Handles the quantity options for the product, determining the initial quantity and available case options.
 *
 * @param {object} productData - The product data.
 * @returns {{ initialQuantity: number, caseTypeOptions: string[] }} - The initial quantity and available case options.
 */
export function getQuantityDetails(productData: any): {
	initialQuantity: number;
	caseTypeOptions: string[];
} {
	const initialQuantity = productData.quantity || 1;
	const caseTypeOptions = productData.caseTypeOptions || ['Mixed', 'Red', 'White'];

	return { initialQuantity, caseTypeOptions };
}

/**
 * Handles the product's strip message, discount, and stock status based on priority.
 * Priority order:
 * 1. Out of stock
 * 2. Message strip
 * 3. Discount
 *
 * Returns the values accordingly or empty strings/0 for rendering control.
 *
 * @param {object} productData - The product data.
 * @param {number} discount - The discount percentage from getPricingDetails.
 * @param {string} stockStatus - The stock status from getStockStatus.
 * @returns {{stripMessage: string, discount: number, stockStatus: string}} -
 * Returns the highest priority value for rendering, or empty/0 where applicable.
 */
export function getPriorityStatus(
	productData: any,
	discount: number,
	stockStatus: string,
): { stripMessage: string; discount: number; stockStatus: string } {
	// Out of stock takes the highest priority
	if (stockStatus === 'out of stock') {
		return { stripMessage: '', discount: 0, stockStatus: 'out of stock' };
	}

	// Message strip has the second-highest priority
	if (productData.stripMessage || productData.availability_state === 'preorder') {
		return {
			stripMessage:
				productData.availability_state === 'preorder'
					? 'Available for pre-order'
					: productData.stripMessage,
			discount: 0,
			stockStatus: '',
		};
	}

	// Discount is the lowest priority
	if (discount > 0) {
		return { stripMessage: '', discount, stockStatus: '' };
	}

	// If none apply, return empty values
	return { stripMessage: '', discount: 0, stockStatus: '' };
}

/**
 * Handles the form submission by processing the product type, quantity, and selected option.
 *
 * This method is called when the form is submitted. It retrieves the product type,
 * quantity, and selected option from the form, logs them to the console, and allows
 * further actions like sending the data via an AJAX request or updating the UI.
 *
 * @param {HTMLElement} targetElement - The product.
 * @param {string} productId - The id of the product.
 * @param {string} productType - The type of product being added to the cart (e.g., default, Mixed).
 * @param {string} quantity - The quantity of the product to be added to the cart.
 * @param {string} selectedOption - The selected product option (e.g., Mixed, Red, White).
 * @param {boolean} isPersonalized - Is this a personalised product?
 * @param {string} depositPrice - The deposit price if present.
 * @returns {void} This function does not return anything, but it handles form submission logic.
 */
export function handleFormSubmission(
	targetElement: HTMLElement,
	productId: number,
	productType: string,
	quantity: number,
	selectedOption: string,
	isPersonalized: boolean,
	depositPrice: string | null,
): void {
	// Handle form data in a way that fits your needs, e.g., an AJAX request, state update, etc.
	if (productType === 'pre_mixed_wine_cases') {
		// console.log('Form submitted with:', {
		// 	productType: productType,
		// 	quantity: quantity,
		// 	caseType: selectedOption,
		// });
	}
	if (productType === 'composite_gifts') {
		// console.log('Form submitted with:', {
		// 	productType: productType,
		// 	quantity: quantity,
		// });
	}

	isPersonalized = false;

	//  selectedOption will override the id
	// This is not a tagManager event
	dispatchAddToBasket({
		targetElement,
		productId,
		productType,
		quantity,
		isPersonalized,
		depositPrice: depositPrice ?? undefined,
	});

	// Example: Add further logic like an AJAX request, UI updates, etc.
	// fetch('/api/add-to-cart', {
	//   method: 'POST',
	//   body: JSON.stringify({ quantity, caseType: selectedOption })
	// })
	// .then(response => response.json())
	// .then(data => console.log('Success:', data))
	// .catch(error => console.error('Error:', error));
}

/**
 * Validates the quantity input field to ensure the value is between 1 and 99.
 * If the value is invalid, it adjusts it accordingly and provides accessible feedback.
 *
 * @private
 * @param {HTMLInputElement} inputElement - The input element to validate.
 * @param {HTMLElement} errorElement - The element used to display error messages for accessibility.
 * @returns {void}
 */
export function validateQuantityInput(
	inputElement: HTMLInputElement,
	errorElement: HTMLElement,
): void {
	const value = parseInt(inputElement.value, 10);

	// If input is empty, revert to the default value of 1
	if (!inputElement.value.trim()) {
		inputElement.value = '1';
		errorElement.textContent = 'Quantity cannot be empty.';
		inputElement.setAttribute('aria-invalid', 'true');
		return;
	}

	// Validate the number within the specified range
	if (isNaN(value) || value < 1) {
		inputElement.value = '1'; // Set to minimum allowed value
		errorElement.textContent = 'Quantity must be at least 1.';
		inputElement.setAttribute('aria-invalid', 'true');
	} else if (value > 99) {
		inputElement.value = '99'; // Set to maximum allowed value
		errorElement.textContent = 'Quantity cannot exceed 99.';
		inputElement.setAttribute('aria-invalid', 'true');
	} else {
		errorElement.textContent = ''; // Clear any errors
		inputElement.setAttribute('aria-invalid', 'false');
	}
}

/**
 * Attaches a click event listener to the select button, toggling the visibility of the options list.
 * It also applies the focus trap when the options list is opened and clears it when closed.
 *
 * @param {HTMLElement} select - The select button element.
 * @param {HTMLElement} optionsList - The options list element that is shown/hidden based on the select button state.
 * @returns {void} This method does not return a value.
 */
export function attachSelectClickListener(select: HTMLElement, optionsList: HTMLElement): void {
	const handleClickOutside = (event: MouseEvent) => {
		const target = event.target as HTMLElement;

		// Close the options list if the click is outside the options or the select button
		if (!optionsList.contains(target) && !select.contains(target)) {
			select.classList.remove('active');
			select.setAttribute('aria-expanded', 'false');
			clearFocusTrap(optionsList); // Clear the focus trap

			// Remove the event listener after closing the list
			document.removeEventListener('click', handleClickOutside);
		}
	};

	select.addEventListener('click', (event: Event) => {
		event.preventDefault();
		event.stopPropagation();

		const isActive = select.classList.contains('active');

		if (isActive) {
			select.classList.remove('active');
			optionsList.hidden = true;
			select.setAttribute('aria-expanded', 'false');
			clearFocusTrap(optionsList); // Clear the focus trap

			// Remove the outside click listener
			document.removeEventListener('click', handleClickOutside);
		} else {
			select.classList.add('active');
			optionsList.hidden = false;
			select.setAttribute('aria-expanded', 'true');

			// Apply focus trap after the transition completes
			optionsList.addEventListener(
				'transitionend',
				() => {
					applyFocusTrap(optionsList);
				},
				{ once: true },
			);

			// Add the outside click listener when the list opens
			document.addEventListener('click', handleClickOutside);
		}
	});
}

/**
 * Attaches event listeners to each dropdown list item to handle selection and accessibility.
 *
 * - When an option is clicked, it updates the selected value in the select button,
 *   hides the options list, updates the `aria-selected` attribute, and clears the focus trap.
 * - Adds keyboard navigation support using arrow keys (Up/Down) to move through options,
 *   Enter/Space to select an option, and Escape to close the options list.
 *
 * @param {NodeListOf<HTMLElement>} selectListItems - The list of dropdown items, each acting as an option.
 * @param {HTMLElement} selectBtn - The button element displaying the currently selected value.
 * @param {HTMLElement} optionsList - The options list element that is shown/hidden based on selection.
 * @returns {void} This method does not return a value.
 */
export function attachSelectListItemListeners(
	selectListItems: NodeListOf<HTMLElement>,
	selectBtn: HTMLElement,
	optionsList: HTMLElement,
): void {
	selectListItems.forEach((item) => {
		item.addEventListener('click', () => {
			const itemValue: string | null = item.getAttribute('data-select-value');
			if (typeof itemValue === 'string') {
				selectBtn.innerText = itemValue;
				selectBtn.dataset.selectedValue = itemValue;
			}

			// Update aria-selected and close the options list
			updateAriaSelected(selectListItems, item);
			selectBtn.classList.remove('active');
			selectBtn.setAttribute('aria-expanded', 'false');
			clearFocusTrap(optionsList);
			selectBtn.focus();
		});

		// Add keyboard navigation support
		item.addEventListener('keydown', (event: KeyboardEvent) => {
			switch (event.key) {
				case 'ArrowDown':
					focusNextItem(selectListItems, item);
					event.preventDefault();
					break;
				case 'ArrowUp':
					focusPreviousItem(selectListItems, item);
					event.preventDefault();
					break;
				case 'Enter':
				case ' ':
					item.click(); // Simulate a click on pressing Enter or Space
					event.preventDefault();
					break;
				case 'Escape':
					selectBtn.click(); // Close the dropdown
					selectBtn.focus();
					event.preventDefault();
					break;
			}
		});
	});
}

/**
 * Updates the `aria-selected` attribute on the dropdown options to reflect the currently selected item.
 *
 * - Sets `aria-selected="false"` on all options to reset them.
 * - Sets `aria-selected="true"` on the newly selected item to indicate it as selected.
 *
 * @param {NodeListOf<HTMLElement>} selectListItems - The list of dropdown items, each representing an option.
 * @param {HTMLElement} selectedItem - The item that was selected and should be marked as `aria-selected="true"`.
 * @returns {void} This method does not return a value.
 */
export function updateAriaSelected(
	selectListItems: NodeListOf<HTMLElement>,
	selectedItem: HTMLElement,
): void {
	selectListItems.forEach((item) => {
		item.setAttribute('aria-selected', 'false');
	});
	selectedItem.setAttribute('aria-selected', 'true');
}

/**
 * Moves the focus to the next item in the dropdown list.
 *
 * - If the current item is not the last, it focuses the next item in the list.
 * - If the current item is the last, it wraps the focus back to the first item.
 *
 * @param {NodeListOf<HTMLElement>} selectListItems - The list of dropdown items to navigate through.
 * @param {HTMLElement} currentItem - The currently focused item from which the navigation starts.
 * @returns {void} This method does not return a value.
 */
export function focusNextItem(
	selectListItems: NodeListOf<HTMLElement>,
	currentItem: HTMLElement,
): void {
	const currentIndex = Array.from(selectListItems).indexOf(currentItem);
	const nextIndex = (currentIndex + 1) % selectListItems.length;
	selectListItems[nextIndex].focus();
}

/**
 * Moves the focus to the previous item in the dropdown list.
 *
 * - If the current item is not the first, it focuses the previous item in the list.
 * - If the current item is the first, it wraps the focus back to the last item.
 *
 * @param {NodeListOf<HTMLElement>} selectListItems - The list of dropdown items to navigate through.
 * @param {HTMLElement} currentItem - The currently focused item from which the navigation starts.
 * @returns {void} This method does not return a value.
 */
export function focusPreviousItem(
	selectListItems: NodeListOf<HTMLElement>,
	currentItem: HTMLElement,
): void {
	const currentIndex = Array.from(selectListItems).indexOf(currentItem);
	const prevIndex = (currentIndex - 1 + selectListItems.length) % selectListItems.length;
	selectListItems[prevIndex].focus();
}
